import { Clock, Send } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth, usePermissions } from '@/features/auth';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { ErrorState } from '@/shared/components/error-state';
import { formatDateTime } from '@/shared/lib/format';

import { ApprovalDecisionDialog } from './approval-decision-dialog';
import { formatParameter, parameterHelp, parameterLabel, parameterUnit } from './pricing-labels';
import {
  useCountryPricing,
  useDecideApproval,
  useSetDraftParameter,
  useSubmitDraft,
} from '../api/pricing.queries';
import type { Approval, CountryPricing } from '../types/pricing.types';

/**
 * A country's pricing parameters: the live set next to the draft. Staff with
 * pricing.config.manage edit the draft and submit it; it goes live only when
 * approved. The draft starts from the live values, so changing one parameter
 * is enough to submit; an unchanged draft cannot be. While a request waits,
 * the draft is locked; a rejection discards it and the editor shows live values.
 */
export function PricingParametersPanel({ countryId }: { countryId: number }) {
  const pricing = useCountryPricing(countryId);
  const { can } = usePermissions();
  const [submitting, setSubmitting] = useState(false);

  if (pricing.isLoading) return <Skeleton className="h-64" />;
  if (pricing.error || !pricing.data) {
    return <ErrorState error={pricing.error} onRetry={() => void pricing.refetch()} />;
  }

  const data = pricing.data;
  const locked = Boolean(data.pendingApproval);
  const canEdit = can('pricing.config.manage') && !locked;
  const liveValue = (parameter: string) =>
    data.active?.parameters.find((p) => p.parameter === parameter)?.value;
  const hasChanges = Boolean(
    data.draft?.parameters.some((p) => p.value !== liveValue(p.parameter)),
  );

  return (
    <div className="flex flex-col gap-3">
      <PendingBanner data={data} />

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-muted-foreground">
          Live version <strong className="text-foreground">{data.active?.version ?? 'none'}</strong>
          {data.active?.effectiveFrom && <> since {formatDateTime(data.active.effectiveFrom)}</>}
          {data.draft && (
            <>
              {' '}
              · draft version <strong className="text-foreground">{data.draft.version}</strong>
            </>
          )}
        </p>
        {can('pricing.config.manage') && !locked && (
          <Button
            size="sm"
            onClick={() => setSubmitting(true)}
            disabled={!data.draft || data.missingFromDraft.length > 0 || !hasChanges}
            title={hasChanges ? undefined : 'Change at least one parameter to submit.'}
          >
            <Send className="size-3.5" aria-hidden />
            Submit draft for approval
          </Button>
        )}
      </div>

      {data.draft && data.missingFromDraft.length > 0 && (
        <p className="text-status-warning text-xs" role="status">
          The draft is missing {data.missingFromDraft.map(parameterLabel).join(', ')}. Set them
          before it can be submitted.
        </p>
      )}

      <div className="border-border overflow-x-auto rounded-md border">
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              <TableHead>Parameter</TableHead>
              <TableHead className="text-end">Live</TableHead>
              <TableHead className="text-end">Draft</TableHead>
              {canEdit && <TableHead className="w-56">Set draft value</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.requiredParameters.map((parameter) => {
              const live = liveValue(parameter);
              const draft = data.draft?.parameters.find((p) => p.parameter === parameter)?.value;
              const changed = draft !== undefined && draft !== live;
              return (
                <TableRow key={parameter}>
                  <TableCell>
                    <span className="font-medium">{parameterLabel(parameter)}</span>
                    <span className="text-muted-foreground block text-[11px]">
                      {parameterHelp(parameter)}
                    </span>
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {formatParameter(parameter, live)}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {draft === undefined ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className={changed ? 'text-status-warning font-semibold' : undefined}>
                        {formatParameter(parameter, draft)}
                        {changed && <span className="sr-only"> (changed)</span>}
                      </span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <DraftValueEditor
                        // Remount when the server value changes (e.g. a rejected draft was
                        // discarded) so the input shows it, not a stale edit.
                        key={`${parameter}:${draft ?? live ?? ''}`}
                        countryId={countryId}
                        parameter={parameter}
                        current={draft ?? live}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {data.history.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer font-medium">
            Earlier versions ({data.history.length})
          </summary>
          <ul className="text-muted-foreground mt-2 space-y-1">
            {data.history.map((set) => (
              <li key={set.version}>
                Version {set.version}: {formatDateTime(set.effectiveFrom)} –{' '}
                {formatDateTime(set.effectiveTo)}
              </li>
            ))}
          </ul>
        </details>
      )}

      {submitting && data.draft && (
        <SubmitDialog
          countryId={countryId}
          version={data.draft.version}
          onClose={() => setSubmitting(false)}
        />
      )}
    </div>
  );
}

function DraftValueEditor({
  countryId,
  parameter,
  current,
}: {
  countryId: number;
  parameter: string;
  current: number | undefined;
}) {
  const [value, setValue] = useState(current === undefined ? '' : String(current));
  const save = useSetDraftParameter(countryId);
  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(parsed);
  const label = `Draft ${parameterLabel(parameter)}${parameterUnit(parameter) ? ` (${parameterUnit(parameter)})` : ''}`;

  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) save.mutate({ parameter, value: parsed });
      }}
    >
      <Input
        aria-label={label}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        type="number"
        step="any"
        className="h-7 w-24 text-xs"
      />
      <Button
        type="submit"
        size="sm"
        variant="outline"
        className="h-7"
        disabled={!valid || save.isPending}
      >
        Save
      </Button>
    </form>
  );
}

/**
 * The request waiting for approval, with the actions the viewer can take on it
 * here — the same rules the API enforces: approvers (pricing.config.approve)
 * approve or reject, but approve their own request only with
 * pricing.config.approve.own; only the requester can withdraw it, and
 * withdrawing (not rejecting) is what the requester is offered.
 */
function PendingBanner({ data }: { data: CountryPricing }) {
  const { can } = usePermissions();
  const { user: me } = useAuth();
  const [deciding, setDeciding] = useState<'approve' | 'reject' | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const pending = data.pendingApproval;
  if (!pending) return null;

  const isRequester = me?.id === pending.requestedByUserId;
  const canDecide = can('pricing.config.approve');
  const canApprove = canDecide && (!isRequester || can('pricing.config.approve.own'));
  // On your own request, withdrawing is the way to take it back, not rejecting it.
  const canReject = canDecide && !isRequester;
  const canWithdraw = isRequester && can('pricing.config.manage');

  return (
    <section
      aria-label="Waiting for approval"
      className="border-status-info-border bg-status-info-bg text-status-info flex flex-wrap items-start gap-x-3 gap-y-2 rounded-md border p-3 text-xs"
    >
      <Clock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p>
          <strong>Waiting for approval:</strong> {pending.summary} — asked by{' '}
          {isRequester ? 'you' : (pending.requestedByName ?? 'a staff member')} on{' '}
          {formatDateTime(pending.requestedAtUtc)}. The draft is locked meanwhile.
        </p>
        {pending.requestReason && <p className="text-foreground">“{pending.requestReason}”</p>}
        {isRequester && canDecide && !canApprove && (
          <p>Another staff member must approve your request.</p>
        )}
        {!canDecide && !isRequester && (
          <p>Someone with pricing approval rights must approve or reject it.</p>
        )}
      </div>

      {(canApprove || canReject || canWithdraw) && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {canApprove && (
            <Button size="sm" className="h-7" onClick={() => setDeciding('approve')}>
              Approve
            </Button>
          )}
          {canReject && (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => setDeciding('reject')}
            >
              Reject
            </Button>
          )}
          {canWithdraw && (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => setWithdrawing(true)}
            >
              Withdraw request
            </Button>
          )}
        </div>
      )}

      {deciding && (
        <ApprovalDecisionDialog
          approval={pending}
          decision={deciding}
          onClose={() => setDeciding(null)}
        />
      )}
      {withdrawing && <WithdrawDialog approval={pending} onClose={() => setWithdrawing(false)} />}
    </section>
  );
}

function WithdrawDialog({ approval, onClose }: { approval: Approval; onClose: () => void }) {
  const decide = useDecideApproval();
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Withdraw this request?"
      description={`${approval.summary}. Nothing goes live, and the draft is unlocked so you can change and resubmit it.`}
      confirmLabel="Withdraw"
      isPending={decide.isPending}
      onConfirm={() =>
        decide.mutate(
          { approvalId: approval.id, decision: 'cancel', reason: null },
          { onSuccess: onClose },
        )
      }
    >
      {decide.error instanceof ApiError && (
        <p role="alert" className="text-destructive text-xs">
          {decide.error.message}
        </p>
      )}
    </ConfirmDialog>
  );
}

function SubmitDialog({
  countryId,
  version,
  onClose,
}: {
  countryId: number;
  version: number;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const submit = useSubmitDraft(countryId);
  const valid = reason.trim().length >= 3;

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Submit version ${version} for approval?`}
      description="It must be approved before it goes live. Customers keep the current prices until then, and the draft is locked while it waits. If it is rejected, the draft is discarded."
      confirmLabel="Submit for approval"
      isPending={submit.isPending}
      confirmDisabled={!valid}
      onConfirm={() => submit.mutate(reason.trim(), { onSuccess: onClose })}
    >
      <div className="space-y-1.5">
        <Label htmlFor="pricing-submit-reason" className="text-xs">
          Reason{' '}
          <span className="text-muted-foreground font-normal">
            (required, shown to the approver)
          </span>
        </Label>
        <Input
          id="pricing-submit-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          className="h-8 text-xs"
        />
        {submit.error instanceof ApiError && (
          <p className="text-destructive text-xs">{submit.error.message}</p>
        )}
      </div>
    </ConfirmDialog>
  );
}
